import * as React from "react";

// 1. Import the block and interface
import * as MainImageBlock from "@/components/layout-blocks/MainImageBlock";
import * as StandardBlock from "@/components/layout-blocks/StandardBlock";
import * as CategoriesCards from "@/components/layout-blocks/CategoriesCards";
import * as ContactBlock from "@/components/layout-blocks/ContactBlock";
import * as CtaBlock from "@/components/layout-blocks/CtaBlock";
import * as HeaderSubpage from "@/components/layout-blocks/HeaderSubpage";
import * as IconBlock from "@/components/layout-blocks/IconBlock";
import * as MainVideoBlock from "@/components/layout-blocks/MainVideoBlock";
import * as TextBoxes from "@/components/layout-blocks/TextBoxes";
import * as SubCategoriesCards from "@/components/layout-blocks/SubCategoriesCards";
import * as LinksNavigation from "@/components/layout-blocks/LinksNavigation";
import * as LatestNews from "@/components/layout-blocks/LatestNews";
import * as AccordionsLarge from "@/components/layout-blocks/AccordionsLarge";
import * as VideoHero from "@/components/layout-blocks/VideoHero";
import * as LatestStart from "@/components/layout-blocks/LatestStart";
import * as LatestEvents from "@/components/layout-blocks/LatestEvents";
import * as AllEvents from "@/components/layout-blocks/AllEvents";
import * as Cards from "@/components/layout-blocks/Cards";
import * as ContactCards from "@/components/layout-blocks/ContactCards";
import * as AccordionsSmall from "@/components/layout-blocks/AccordionsSmall";
import * as ImageBlock from "@/components/layout-blocks/ImageBlock";
import * as FormGiftfunds from "@/components/layout-blocks/FormGiftfunds";
import * as FormSuggestions from "@/components/layout-blocks/FormSuggestions";
import * as FormBookLocation from "@/components/layout-blocks/FormBookLocation";
import * as PriceCards from "@/components/layout-blocks/PriceCards";
import * as VideoCards from "@/components/layout-blocks/VideoCards";
import * as FormContributionSection from "@/components/layout-blocks/FormContributionSection";
import * as FormWibelfonden from "@/components/layout-blocks/FormWibelfonden";
import { BreadCrumbProps } from "@/components/global-components/Breadcrumbs";

// 2. Add enum BlockType
export enum BlockType {
  MainImageBlock,
  StandardBlock,
  CategoriesCards,
  ContactBlock,
  CtaBlock,
  HeaderSubpage,
  IconBlock,
  MainVideoBlock,
  TextBoxes,
  SubCategoriesCards,
  LinksNavigation,
  LatestNews,
  AccordionsLarge,
  VideoHero,
  LatestStart,
  LatestEvents,
  AllEvents,
  Cards,
  ContactCards,
  AccordionsSmall,
  ImageBlock,
  FormGiftfunds,
  FormSuggestions,
  FormBookLocation,
  PriceCards,
  VideoCards,
  FormContributionSection,
  FormWibelfonden,
}

// 3. Add layout-block interface
export interface IComponent
  extends MainImageBlock.Props,
    StandardBlock.Props,
    CategoriesCards.CategoriesCardProps,
    ContactBlock.ContactBlockProps,
    CtaBlock.CtaBlockProps,
    HeaderSubpage.HeaderSubpageProps,
    IconBlock.IconBlockProps,
    MainVideoBlock.MainVideoBlockProps,
    TextBoxes.TextBoxesProps,
    SubCategoriesCards.SubCategoriesCardsProps,
    LinksNavigation.LinksNavigationProps,
    LatestNews.LatestNewsProps,
    AccordionsLarge.AccordionsLargeProps,
    VideoHero.VideoHeroProps,
    LatestStart.LatestStartProps,
    LatestEvents.LatestEventsProps,
    AllEvents.AllEventsProps,
    Cards.CardsProps,
    ContactCards.ContactBlockProps,
    AccordionsSmall.AccordionsSmallProps,
    ImageBlock.ImageBlockProps,
    FormGiftfunds.FormGiftfundsProps,
    FormSuggestions.FormSuggestionsProps,
    FormBookLocation.FormBookLocationProps,
    PriceCards.PriceCardsProps,
    VideoCards.VideoCardsProps,
    FormContributionSection.FormContributionSectionProps,
    FormWibelfonden.FormWibelfondenProps {
  label: keyof typeof BlockType;
}

export interface IFlexible {
  components: ReadonlyArray<IComponent>;
  breadcrumb?: BreadCrumbProps;
}

// 4. add layout-block as [BlockType.Example]: Example,
const BlockComponents = {
  [BlockType.MainImageBlock]: MainImageBlock.default,
  [BlockType.StandardBlock]: StandardBlock.default,
  [BlockType.CategoriesCards]: CategoriesCards.default,
  [BlockType.ContactBlock]: ContactBlock.default,
  [BlockType.CtaBlock]: CtaBlock.default,
  [BlockType.HeaderSubpage]: HeaderSubpage.default,
  [BlockType.IconBlock]: IconBlock.default,
  [BlockType.MainVideoBlock]: MainVideoBlock.default,
  [BlockType.TextBoxes]: TextBoxes.default,
  [BlockType.SubCategoriesCards]: SubCategoriesCards.default,
  [BlockType.LinksNavigation]: LinksNavigation.default,
  [BlockType.LatestNews]: LatestNews.default,
  [BlockType.AccordionsLarge]: AccordionsLarge.default,
  [BlockType.VideoHero]: VideoHero.default,
  [BlockType.LatestStart]: LatestStart.default,
  [BlockType.LatestEvents]: LatestEvents.default,
  [BlockType.AllEvents]: AllEvents.default,
  [BlockType.Cards]: Cards.default,
  [BlockType.ContactCards]: ContactCards.default,
  [BlockType.AccordionsSmall]: AccordionsSmall.default,
  [BlockType.ImageBlock]: ImageBlock.default,
  [BlockType.FormGiftfunds]: FormGiftfunds.default,
  [BlockType.FormSuggestions]: FormSuggestions.default,
  [BlockType.FormBookLocation]: FormBookLocation.default,
  [BlockType.PriceCards]: PriceCards.default,
  [BlockType.VideoCards]: VideoCards.default,
  [BlockType.FormContributionSection]: FormContributionSection.default,
  [BlockType.FormWibelfonden]: FormWibelfonden.default,
};

const Flexible: React.FC<IFlexible> = ({ components, breadcrumb }) => {
  return (
    <React.Fragment>
      {components
        ?.filter((c) => c)
        .filter((c) => c.label)
        .filter((c) => BlockType[c.label] !== undefined)
        .map((comp, index) => {
          const Component = BlockComponents[BlockType[comp.label]];

          return <Component key={index} {...comp} breadcrumb={breadcrumb} />;
        })}
    </React.Fragment>
  );
};

export default Flexible;
